import { type RefObject, useMemo, useRef } from 'react';
import { useABSmartlyContext } from '../context/ABSmartlyProvider';
import { getVariantsArray } from '../utils/variants';
type ReturnType<TVariants extends readonly string[]> = {
  decisions: RefObject<Record<string, number | null>>;
  isReady: boolean;
  singleDecision: RefObject<null | number>;
  results: (TVariants[number] | null)[];
};
export const useClientABSmartlyExperiments = <TVariants extends readonly string[],>(experimentIds: string | string[]): ReturnType<TVariants> => {
  const context = useABSmartlyContext();
  const treatments = Array.isArray(experimentIds) ? experimentIds.reduce((acc, experimentId) => {
    return {
      ...acc,
      [experimentId]: null
    };
  }, {} as Record<string, number | null>) : {};
  const singleDecision = useRef<null | number>(null);
  const decisions = useRef<Record<string, number | null>>(treatments);
  return useMemo(() => {
    const evaluateExperiment = (experimentId_0: string) => {
      if (!experimentId_0 || !context || !context.isReady()) {
        return null;
      }
      const variant = context.treatment(experimentId_0);
      if (Array.isArray(experimentIds)) {
        decisions.current = {
          ...decisions.current,
          [experimentId_0]: variant
        };
      } else {
        singleDecision.current = variant;
      }
      const variants = getVariantsArray(context, experimentId_0);
      return variants[variant] as TVariants[number] | undefined ?? null;
    };
    const results: (TVariants[number] | null)[] = Array.isArray(experimentIds) ? experimentIds.map(experimentId_1 => evaluateExperiment(experimentId_1)) : [evaluateExperiment(experimentIds)];
    return {
      isReady: !!context?.isReady(),
      results,
      singleDecision,
      decisions
    };
  }, [context, experimentIds]);
};